// components/leads/NewLeadModal.js
// Modal per creare un nuovo lead

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { User, Building2, MapPin, HardHat } from 'lucide-react';

const NewLeadModal = ({
  open,
  onOpenChange,
  newLead,
  setNewLead,
  onSubmit,
  onClose,
  isSubmitting = false
}) => {
  // Handler per toggle cantiere coincide con residenza
  const handleSiteSameToggle = (checked) => {
    setNewLead(prev => ({
      ...prev,
      site_same_as_customer_address: checked,
      // Se attivo, pulisci campi cantiere
      ...(checked ? {
        site_address: '',
        site_city: '',
        site_cap: '',
        site_province: '',
        site_notes: ''
      } : {})
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nuovo Lead</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={onSubmit}>
          <div className="space-y-6 py-4">
            {/* Business toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Cliente Business</Label>
                <p className="text-sm text-[#737373]">Attiva per inserire dati aziendali</p>
              </div>
              <Switch
                checked={newLead.is_business}
                onCheckedChange={(checked) => setNewLead(prev => ({ ...prev, is_business: checked }))}
              />
            </div>

            {/* Business fields */}
            {newLead.is_business && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-[#FF7819]/20 rounded-lg bg-[#FFF5EE]">
                <div className="col-span-2">
                  <Label>Ragione Sociale</Label>
                  <Input
                    value={newLead.business_name}
                    onChange={(e) => setNewLead(prev => ({ ...prev, business_name: e.target.value }))}
                    placeholder="Es. ABITALO Srl"
                    data-testid="new-lead-business-name"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Partita IVA</Label>
                  <Input
                    value={newLead.vat_number || ''}
                    onChange={(e) => setNewLead(prev => ({ ...prev, vat_number: e.target.value }))}
                    placeholder="Es. IT12345678901"
                    data-testid="new-lead-vat-number"
                  />
                </div>
              </div>
            )}
            
            {/* Personal data */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nome <span className="text-red-500">*</span></Label>
                <Input
                  value={newLead.first_name}
                  onChange={(e) => setNewLead(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Es. Mario"
                  required
                  data-testid="new-lead-first-name"
                />
              </div>
              <div>
                <Label>Cognome <span className="text-red-500">*</span></Label>
                <Input
                  value={newLead.last_name}
                  onChange={(e) => setNewLead(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Es. Rossi"
                  required
                  data-testid="new-lead-last-name"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="Es. mario.rossi@email.com"
                  data-testid="new-lead-email"
                />
              </div>
              <div>
                <Label>Telefono <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Es. 333 1234567"
                  required
                  data-testid="new-lead-phone"
                />
              </div>
              {/* Codice Fiscale - opzionale, richiesto per contratto */}
              <div className="col-span-2">
                <Label>
                  Codice Fiscale 
                  <span className="text-[#737373] text-xs ml-2">(opzionale, richiesto per contratto)</span>
                </Label>
                <Input
                  value={newLead.fiscal_code || ''}
                  onChange={(e) => setNewLead(prev => ({ ...prev, fiscal_code: e.target.value.toUpperCase() }))}
                  placeholder="Es. RSSMRA80A01H501U"
                  maxLength={16}
                  data-testid="new-lead-fiscal-code"
                />
              </div>
            </div>
            
            {/* === BLOCCO A: Indirizzo Cliente (Residenza/Domicilio) === */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-[#FF7819]" />
                <Label className="text-base font-medium">Indirizzo Cliente (Residenza/Domicilio)</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Indirizzo <span className="text-red-500">*</span></Label>
                  <Input
                    value={newLead.customer_address || ''}
                    onChange={(e) => setNewLead(prev => ({ ...prev, customer_address: e.target.value }))}
                    placeholder="Es. Via Roma 123"
                    required
                    data-testid="new-lead-customer-address"
                  />
                </div>
                <div>
                  <Label>Città</Label>
                  <Input
                    value={newLead.customer_city || ''}
                    onChange={(e) => setNewLead(prev => ({ ...prev, customer_city: e.target.value }))}
                    placeholder="Es. Bari"
                    data-testid="new-lead-customer-city"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>CAP</Label>
                    <Input
                      value={newLead.customer_cap || ''}
                      onChange={(e) => setNewLead(prev => ({ ...prev, customer_cap: e.target.value }))}
                      placeholder="70100"
                      data-testid="new-lead-customer-cap"
                    />
                  </div>
                  <div>
                    <Label>Provincia</Label>
                    <Input
                      value={newLead.customer_province || ''}
                      onChange={(e) => setNewLead(prev => ({ ...prev, customer_province: e.target.value.toUpperCase() }))}
                      placeholder="BA"
                      maxLength={2}
                      data-testid="new-lead-customer-province"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            {/* === BLOCCO B: Indirizzo Cantiere (Immobile oggetto lavori) === */}
            <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <HardHat className="w-5 h-5 text-[#FF7819]" />
                <Label className="text-base font-medium">Indirizzo Cantiere (Immobile oggetto lavori)</Label>
              </div>
              
              {/* Checkbox coincidenza */}
              <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
                <Checkbox
                  id="site-same-as-customer"
                  checked={newLead.site_same_as_customer_address !== false}
                  onCheckedChange={handleSiteSameToggle}
                  data-testid="new-lead-site-same-checkbox"
                />
                <label
                  htmlFor="site-same-as-customer"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Il cantiere coincide con l'indirizzo di residenza del cliente
                </label>
              </div>
              
              {/* Campi cantiere (visibili solo se checkbox disattivo) */}
              {newLead.site_same_as_customer_address === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="col-span-2">
                    <Label>Indirizzo Cantiere <span className="text-red-500">*</span></Label>
                    <Input
                      value={newLead.site_address || ''}
                      onChange={(e) => setNewLead(prev => ({ ...prev, site_address: e.target.value }))}
                      placeholder="Es. Via Napoli 45"
                      required={newLead.site_same_as_customer_address === false}
                      data-testid="new-lead-site-address"
                    />
                  </div>
                  <div>
                    <Label>Città</Label>
                    <Input
                      value={newLead.site_city || ''}
                      onChange={(e) => setNewLead(prev => ({ ...prev, site_city: e.target.value }))}
                      placeholder="Es. Bari"
                      data-testid="new-lead-site-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>CAP</Label>
                      <Input
                        value={newLead.site_cap || ''}
                        onChange={(e) => setNewLead(prev => ({ ...prev, site_cap: e.target.value }))}
                        placeholder="70100"
                        data-testid="new-lead-site-cap"
                      />
                    </div>
                    <div>
                      <Label>Provincia</Label>
                      <Input
                        value={newLead.site_province || ''}
                        onChange={(e) => setNewLead(prev => ({ ...prev, site_province: e.target.value.toUpperCase() }))}
                        placeholder="BA"
                        maxLength={2}
                        data-testid="new-lead-site-province"
                      />
                    </div>
                  </div>
                  <div className="col-span-2">
                    <Label>Note accesso cantiere</Label>
                    <Textarea
                      value={newLead.site_notes || ''}
                      onChange={(e) => setNewLead(prev => ({ ...prev, site_notes: e.target.value }))}
                      placeholder="Es. Citofono Rossi, portone sulla destra, piano 3..."
                      rows={2}
                      data-testid="new-lead-site-notes"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Source */}
            <div>
              <Label>Provenienza</Label>
              <Input
                value={newLead.source}
                onChange={(e) => setNewLead(prev => ({ ...prev, source: e.target.value }))}
                placeholder="Es. Sito web, Passaparola, Facebook..."
                data-testid="new-lead-source"
              />
            </div>
            
            {/* Notes */}
            <div>
              <Label>Note</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Note aggiuntive..."
                rows={3}
                data-testid="new-lead-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annulla
            </Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              data-testid="submit-new-lead"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creazione...' : 'Crea Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewLeadModal;
